import * as React from "react";
import { NavigationIcon } from "../../../components/icons";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/feedback";
import { Tooltip } from "../../../components/ui/tooltip";
import { cn } from "../../../lib/utils";
import { EditInfo } from "../../../platform";

interface EditCardProps {
  edit: EditInfo;
  editIndex: number;
  messageIndex: number;
  /** Highlighted as the edit the document is currently showing. */
  isActive: boolean;
  /**
   * Whether this host can accept or reject an applied edit. False in Excel and
   * PowerPoint, which have no way back -- the card still says what changed, it just
   * offers no decision.
   */
  canReview: boolean;
  /** Whether this host can move to an edit at all. False in Outlook. */
  canNavigate: boolean;
  onJumpToEdit: (messageIndex: number, editIndex: number) => void;
  onAcceptEdit: (messageIndex: number, editIndex: number) => void;
  onRejectEdit: (messageIndex: number, editIndex: number) => void;
}

/**
 * One applied change, as the chat shows it.
 *
 * The removed text is struck through in the error colour and the inserted text is in
 * the success colour, because that is what "deleted" and "added" have meant in every
 * diff since diff. The insertion used to be link blue, which read as something to
 * click. The strikethrough is shown only where something was actually replaced or
 * removed: an insertion has no "before" (an empty `find`), and striking through a
 * placeholder like "[insert after]" reads as though text was deleted when nothing was.
 *
 * The text is clamped to five lines so a long insertion does not push the chat off
 * screen, with Show all where the clamp actually cut something.
 */
const EditCard: React.FC<EditCardProps> = ({
  edit,
  editIndex,
  messageIndex,
  isActive,
  canReview,
  canNavigate,
  onJumpToEdit,
  onAcceptEdit,
  onRejectEdit,
}) => {
  const highlighted = isActive && edit.status === "pending";
  const showStrikethrough = edit.find !== "" && !edit.find.startsWith("[");

  const textRef = React.useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = React.useState(false);
  const [clamped, setClamped] = React.useState(false);

  // Measured rather than guessed from the length: whether five lines overflow depends
  // on the pane's width, which the user can drag. Not re-measured while expanded, so
  // Show less stays on offer.
  React.useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || expanded) return undefined;
    const measure = () => setClamped(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [edit.find, edit.replace, expanded]);

  // No anchor means nothing to navigate to and nothing to accept -- a Word comment is
  // the case that matters, since it is a real change with no revision behind it.
  const reviewable = canReview && edit.status === "pending" && Boolean(edit.bookmarkName);
  const canJump = canNavigate && Boolean(edit.bookmarkName || edit.segmentId);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border bg-card transition-[border-color,box-shadow] duration-150",
        // The ring is a 2px halo of the accent tint around the accent border: the
        // card the document is showing, without making it louder than the reply.
        highlighted ? "border-primary ring-2 ring-primary-tint" : "border-border",
        edit.status === "rejected" && "opacity-60"
      )}
    >
      <div className="flex items-center gap-1.5 pt-1.5 pr-1.5 pl-2.5">
        <span className="min-w-0 flex-1 truncate text-[12px] leading-[1.3] font-semibold text-strong">
          {edit.label}
        </span>
        {edit.status !== "pending" && (
          <Badge
            className={cn(
              "h-[18px] text-[10.5px]",
              edit.status === "accepted"
                ? "bg-success-tint text-success-dark"
                : "bg-grey-100 text-muted-foreground"
            )}
          >
            {edit.status === "accepted" ? "Accepted" : "Dismissed"}
          </Badge>
        )}
        {canJump && (
          <Tooltip title="Show in document" side="top">
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Show in document"
              onClick={() => onJumpToEdit(messageIndex, editIndex)}
              className={cn(
                "size-[22px]",
                highlighted
                  ? "text-primary hover:text-primary"
                  : "text-disabled hover:text-disabled"
              )}
            >
              <NavigationIcon className="size-3.5" />
            </Button>
          </Tooltip>
        )}
      </div>

      <div className={cn("mx-2.5 mt-1", reviewable ? "mb-0" : "mb-2.5")}>
        <div
          ref={textRef}
          className={cn(
            "rounded-[12px] bg-grey-50 px-2.5 py-2 text-[12px] leading-[1.6] whitespace-pre-wrap [word-break:break-word]",
            !expanded && "line-clamp-5"
          )}
        >
          {showStrikethrough && (
            <>
              <span className="text-error line-through decoration-error-light">{edit.find}</span>
              {"  "}
            </>
          )}
          {edit.replace !== "[deleted]" && (
            <span
              className={cn(
                "rounded-xs px-0.5 box-decoration-clone",
                edit.status === "rejected"
                  ? "bg-transparent text-disabled"
                  : "bg-success-tint text-success-dark"
              )}
            >
              {edit.replace}
            </span>
          )}
        </div>
        {clamped && (
          <Button
            variant="link"
            aria-expanded={expanded}
            onClick={() => setExpanded((open) => !open)}
            className="mt-1 h-auto text-[11.5px]"
          >
            {expanded ? "Show less" : "Show all"}
          </Button>
        )}
      </div>

      {reviewable && (
        <div className="flex justify-end gap-1 p-2">
          <Button
            variant="ghost"
            onClick={() => onRejectEdit(messageIndex, editIndex)}
            className="text-muted-foreground hover:text-muted-foreground"
          >
            Dismiss
          </Button>
          <Button onClick={() => onAcceptEdit(messageIndex, editIndex)}>Accept</Button>
        </div>
      )}
    </div>
  );
};

export default EditCard;
